import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { map } from 'lodash';
import Question from '../assessment/Question';
import {
    FlexDiv,
    SpaceBetweenDiv,
    ThemedButton,
} from '@awarego-ui/components';
import '../assessment/Question.scss';
import './owasp-training-demo.scss';

function filterBlockValues(blockValues) {
    const result = {};
    map(blockValues, (v, k) => {
        if (v != null && k !== null && k !== 'undefined') result[k] = v;
    });
    return result;
}

export default function OwaspTrainingQuiz({ question, onFinish, onRestart }) {
    const [blockValues, setBlockValues] = useState({});
    const [completedBlocks, setCompletedBlocks] = useState([]);
    const [blockNum, setBlockNum] = useState(0);
    const [blockFadeIn, setBlockFadeIn] = useState(true);
    const [blockFadeOut, setBlockFadeOut] = useState(false);

    const ui = useMemo(() => question?.definition?.ui, [question]);

    const blockList = useMemo(
        () => (ui && ui.blocks ? ui.blocks : []),
        [ui]
    );

    useEffect(() => {
        setBlockValues({});
        setBlockNum(0);
        setCompletedBlocks([]);
    }, [question?.id]);

    useEffect(() => {
        if (!ui?.blocks) {
            setCompletedBlocks([]);
            return;
        }
        const filtered = filterBlockValues(blockValues);
        const completed = ui.blocks.map((block) => {
            const x = filtered[block.actionId];
            return typeof x === 'string' && x.length > 0;
        });
        setCompletedBlocks(completed);
    }, [blockValues, ui]);

    const handleBlockChange = (value, actionId) => {
        const id =
            actionId || (blockList[blockNum] && blockList[blockNum].actionId);
        if (!id) return;
        setBlockValues((prev) =>
            filterBlockValues({ ...prev, [id]: value })
        );
    };

    const handleBlockChangeFromRadio = (value) => {
        handleBlockChange(value);
    };

    const onQuestionAction = () => {};

    const hasNextBlock = () =>
        blockList && blockNum < blockList.length - 1 && blockList[blockNum];

    const hasPreviousBlock = () => blockNum !== 0;

    const currentBlockAnswered = () => {
        const block = blockList[blockNum];
        if (!block) return false;
        const v = blockValues[block.actionId];
        return typeof v === 'string' && v.length > 0;
    };

    const onNext = () => {
        if (hasNextBlock()) {
            setBlockFadeOut(true);
            setTimeout(() => {
                setBlockNum((n) => Math.min(n + 1, blockList.length - 1));
                setBlockFadeOut(false);
                setBlockFadeIn(true);
            }, 300);
            return;
        }
        if (currentBlockAnswered()) {
            onFinish(blockValues);
        }
    };

    const onPrevious = () => {
        if (hasPreviousBlock()) {
            setBlockNum((n) => Math.max(n - 1, 0));
        }
    };

    const isLastBlock = blockNum === blockList.length - 1;
    const canProceed = currentBlockAnswered();

    if (!ui?.blocks) return null;

    return (
        <Fragment>
            <div className="owasp-demo-quiz-header">
                <button type="button" className="owasp-demo-link" onClick={onRestart}>
                    ← Start over
                </button>
                <span>
                    Question {blockNum + 1} of {blockList.length}
                </span>
            </div>

            <div className="owasp-demo-quiz assessment-preview-question">
                <Question
                    handleBlockChange={handleBlockChangeFromRadio}
                    question={question}
                    blockNum={blockNum}
                    onPrevBlock={onPrevious}
                    onNextBlock={onNext}
                    blockValues={blockValues}
                    completedBlocks={completedBlocks}
                    userName=""
                    userEmail=""
                    onQuestionAction={onQuestionAction}
                    visibleBlocks={ui.blocks}
                    blockFadeIn={blockFadeIn}
                    blockFadeOut={blockFadeOut}
                />
            </div>

            <div className="owasp-demo-footer modalFooter">
                <SpaceBetweenDiv alignCenter justifyCenter>
                    <FlexDiv>
                        <ThemedButton
                            mode="secondary"
                            disabled={!hasPreviousBlock()}
                            onClick={onPrevious}
                            text="Back"
                        />
                        <ThemedButton
                            mode="primary"
                            $airy
                            disabled={!canProceed}
                            onClick={onNext}
                            text={isLastBlock ? 'See results' : 'Next'}
                        />
                    </FlexDiv>
                </SpaceBetweenDiv>
            </div>
        </Fragment>
    );
}
